import React, { useState } from 'react';
import { Check, Lock, Unlock, Pencil, Star, Trash2, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ImageGrid = ({
  images,
  selectedImageBg,
  defaultImageId,
  uploadingImage,
  selectedAnimation,
  onImageBgSelect,
  onImageRename,
  onImageToggleLock,
  onImageDelete,
  onSetDefaultImage,
  onImageUpload,
  onAnimationChange,
  testIdPrefix = ''
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
        {images.map((img, index) => (
          <div
            key={img.id}
            onClick={() => onImageBgSelect(index)}
            className={`relative rounded-lg overflow-hidden aspect-video border-2 transition-all group cursor-pointer ${
              selectedImageBg === index
                ? 'border-amber-400 ring-2 ring-amber-400/30'
                : 'border-transparent hover:border-white/30'
            }`}
            data-testid={`${testIdPrefix}image-bg-btn-${img.id}`}
          >
            <img
              src={img.url.startsWith('http') ? img.url : `${API}${img.url}`}
              alt={img.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 flex items-center gap-1">
              {editingId === img.id ? (
                <input
                  autoFocus
                  className="text-white text-xs bg-white/20 rounded px-1 py-0.5 w-full outline-none"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={() => {
                    if (editingName.trim() && editingName !== img.name) onImageRename(img.id, editingName.trim());
                    setEditingId(null);
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingId(null); }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`${testIdPrefix}image-rename-input-${img.id}`}
                />
              ) : (
                <p className="text-white text-xs truncate flex-1">{img.name}</p>
              )}
              {img.locked && <Lock className="w-3 h-3 text-amber-400 shrink-0" />}
            </div>
            {selectedImageBg === index && (
              <div className="absolute top-1 right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            {defaultImageId === img.id && selectedImageBg !== index && (
              <div className="absolute top-1 right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                <Star className="w-3 h-3 text-white fill-white" />
              </div>
            )}
            <div className="absolute top-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button onClick={(e) => { e.stopPropagation(); onSetDefaultImage(img.id); }}
                className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${defaultImageId === img.id ? 'bg-amber-500/80' : 'bg-black/60 hover:bg-black/80'}`}
                title="Image par défaut" data-testid={`${testIdPrefix}image-default-btn-${img.id}`}>
                <Star className={`w-3 h-3 ${defaultImageId === img.id ? 'text-white fill-white' : 'text-white/60'}`} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setEditingId(img.id); setEditingName(img.name); }}
                className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                title="Renommer" data-testid={`${testIdPrefix}image-rename-btn-${img.id}`}>
                <Pencil className="w-3 h-3 text-white" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onImageToggleLock(img); }}
                className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                title={img.locked ? 'Déverrouiller' : 'Verrouiller'} data-testid={`${testIdPrefix}image-lock-btn-${img.id}`}>
                {img.locked ? <Lock className="w-3 h-3 text-amber-400" /> : <Unlock className="w-3 h-3 text-white/60" />}
              </button>
              {!img.locked && (
                <button onClick={(e) => { e.stopPropagation(); onImageDelete(img); }}
                  className="w-6 h-6 rounded-full bg-red-500/60 flex items-center justify-center hover:bg-red-500/80 transition-colors"
                  title="Supprimer" data-testid={`${testIdPrefix}image-delete-btn-${img.id}`}>
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 bg-white/5 rounded-xl">
        <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Ajouter une image</p>
        <div className="flex gap-2 mb-2">
          <select value={selectedAnimation} onChange={(e) => onAnimationChange(e.target.value)}
            className="flex-1 px-2 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-xs">
            <option value="breathe">Respiration</option>
            <option value="zoom-slow">Zoom lent</option>
            <option value="pan-right">Pan droite</option>
            <option value="pan-left">Pan gauche</option>
            <option value="pulse-glow">Pulsation</option>
            <option value="float">Flottante</option>
          </select>
        </div>
        <label className="cursor-pointer">
          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
            onChange={(e) => { const file = e.target.files[0]; if (file) onImageUpload(file); }}
            disabled={uploadingImage} />
          <div className={`w-full py-3 rounded-lg border-2 border-dashed transition-all flex items-center justify-center gap-2 ${
            uploadingImage ? 'border-white/20' : 'border-white/30 hover:border-amber-400'}`}>
            {uploadingImage ? (
              <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            ) : (<><Upload className="w-4 h-4 text-white/50" /><span className="text-white/70 text-xs">Cliquez pour ajouter</span></>)}
          </div>
        </label>
      </div>
    </div>
  );
};

export default ImageGrid;
